import { HttpClient } from '@adapters/HttpClient';
import {
  IncidentMapping,
  CreateMappingPayload,
  UpdateMappingPayload,
  MappingFilters,
} from '../models/Mapping';
import { IncidentType } from '../models/Mapping';
import { CourierIncidentKind } from '../models/Mapping';

export interface MappingsPage {
  data: IncidentMapping[];
  meta: {
    currentPage: number;
    lastPage: number;
    total: number;
    perPage: number;
  };
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
}

export class MappingsApiAdapter {
  constructor(private httpClient: HttpClient) {}

  /**
   * Obtiene el listado paginado de mapeos
   */
  async getMappings(filters: MappingFilters = {}): Promise<MappingsPage> {
    const {
      courier_id,
      status,
      page = 1,
      per_page = 25,
      sortBy = 'id',
      sortDirection = 'desc',
    } = filters;

    const params: Record<string, any> = {
      page,
      per_page: Math.min(per_page, 100), // max 100
    };

    // Ordenamiento
    if (sortBy) {
      params.order_by = sortBy;
    }
    if (sortDirection) {
      params.order_direction = sortDirection;
    }

    // Filtros: solo courier_id y status
    if (courier_id) {
      params.courier_id = courier_id;
    }

    if (status) {
      params.status = status;
    }

    // Solo mostrar loading global para la primera página
    const showGlobalLoading = page === 1;
    const response = await this.httpClient.get<any>(
      '/mappings',
      params,
      {},
      showGlobalLoading
    );

    // La API devuelve { items: [...] }
    const rawItems = response?.items ?? [];
    
    // Mapear los items de la API al formato IncidentMapping
    const data: IncidentMapping[] = rawItems.map((item: any) => {
      const mapping: IncidentMapping = {
        id: item.id ?? null,
        courier_id: item.courier_incident_kind?.courier_id ?? 0,
        courier_incident_kind_id: item.courier_incident_kind?.id ?? 0,
        incident_type_id: item.incident_type?.id ?? null,
        status: item.is_mapped ? 'mapped' : 'unmapped',
        created_at: item.created_at,
        updated_at: item.updated_at,
      };

      // Mapear courier_incident_kind si existe
      if (item.courier_incident_kind) {
        mapping.courier_incident_kind = {
          id: item.courier_incident_kind.id,
          external_code: item.courier_incident_kind.external_code ?? '',
          external_label: item.courier_incident_kind.external_label ?? '',
          courier_id: item.courier_incident_kind.courier_id,
        };
      }

      // Mapear incident_type si existe
      if (item.incident_type) {
        mapping.incident_type = {
          id: item.incident_type.id,
          code: item.incident_type.code ?? '',
          name: item.incident_type.label ?? '',
          label: item.incident_type.label ?? '',
        };
      }

      return mapping;
    });

    // Calcular metadata de paginación
    // Si la API devuelve metadata, usarla; si no, calcularla basándose en los items recibidos
    const total = response?.meta?.total ?? response?.total ?? data.length;
    const meta = response?.meta ?? {
      currentPage: page,
      lastPage: Math.ceil(total / per_page) || 1,
      total: total,
      perPage: per_page,
    };

    return {
      data,
      meta,
    };
  }

  /**
   * Crea un nuevo mapeo
   */
  async createMapping(
    payload: CreateMappingPayload
  ): Promise<ApiResponse<IncidentMapping>> {
    console.log('🔍 MappingsApiAdapter - createMapping:', { payload });

    const response = await this.httpClient.post<ApiResponse<IncidentMapping>>(
      '/mappings',
      payload
    );

    console.log('🔍 MappingsApiAdapter - Create response:', response);

    return {
      success: response?.success ?? true,
      message: response?.message,
      data: response?.data,
    };
  }

  /**
   * Actualiza un mapeo existente
   */
  async updateMapping(
    id: number,
    payload: UpdateMappingPayload
  ): Promise<ApiResponse<IncidentMapping>> {
    console.log('🔍 MappingsApiAdapter - updateMapping:', { id, payload });

    const response = await this.httpClient.put<ApiResponse<IncidentMapping>>(
      `/mappings/${id}`,
      payload
    );

    console.log('🔍 MappingsApiAdapter - Update response:', response);

    return {
      success: response?.success ?? true,
      message: response?.message,
      data: response?.data,
    };
  }

  /**
   * Obtiene los tipos de incidencias internos (catálogo)
   */
  async getIncidentTypes(): Promise<IncidentType[]> {
    const response = await this.httpClient.get<any>('/incident-types');

    // La API devuelve { items: [...] }
    if (response?.items && Array.isArray(response.items)) {
      return response.items;
    }
    
    // Fallback: si viene como array directo o en data
    if (Array.isArray(response)) {
      return response;
    }
    
    if (Array.isArray(response?.data)) {
      return response.data;
    }
    
    return [];
  }

  /**
   * Obtiene los tipos de incidencias de un courier específico (catálogo)
   */
  async getCourierIncidentKinds(
    courierId: number
  ): Promise<CourierIncidentKind[]> {
    const response = await this.httpClient.get<any>(
      `/couriers/${courierId}/incident-kinds`
    );

    console.log('🔍 MappingsApiAdapter - getCourierIncidentKinds response:', response);

    // La API devuelve { courier_id: number, items: [...] }
    if (response?.items && Array.isArray(response.items)) {
      const mapped = response.items.map((item: any) => ({
        id: item.id,
        external_code: item.external_code || item.code || '',
        external_label: item.external_label || item.description || item.external_code || '',
        courier_id: item.courier_id,
      }));
      console.log('🔍 MappingsApiAdapter - mapped items:', mapped);
      return mapped;
    }
    
    // Fallback: si viene como array directo o en data
    if (Array.isArray(response)) {
      const mapped = response.map((item: any) => ({
        id: item.id,
        external_code: item.external_code || item.code || '',
        external_label: item.external_label || item.description || item.external_code || '',
        courier_id: item.courier_id,
      }));
      console.log('🔍 MappingsApiAdapter - mapped array:', mapped);
      return mapped;
    }
    
    if (Array.isArray(response?.data)) {
      const mapped = response.data.map((item: any) => ({
        id: item.id,
        external_code: item.external_code || item.code || '',
        external_label: item.external_label || item.description || item.external_code || '',
        courier_id: item.courier_id,
      }));
      console.log('🔍 MappingsApiAdapter - mapped data:', mapped);
      return mapped;
    }
    
    console.log('🔍 MappingsApiAdapter - returning empty array');
    return [];
  }
}
